<template>
  <div v-loading="chartLoading">
    <highcharts class="chart" :options="options" />
  </div>
</template>

<script>
import { isEmpty, forEach, find } from 'lodash';
import msgUtil from '@/utils/message';
import { chartColorSet } from '@/constants/chart-options';
import LogAPI from '@/api/log/integrated-log';
import { getIntervalDatetimeParams, getDateToString } from '@/utils/date';
import { convertValueByUnitData } from '@/utils/number';

export default {
  name: 'WDetailAlramChart',
  props: {
    eventInfo: Object,
  },
  data() {
    return {
      chartLoading: false,
      options: {
        chart: {
          backgroundColor: 'transparent',
          spacingTop: 5,
          spacingBottom: 0,
          zoomType: 'x',
          height: 160,
        },
        title: false,
        credits: false,
        xAxis: {
          type: 'datetime',
          minRange: 5 * 60 * 1000,
          labels: {
            format: '{value: %Y-%m-%d %H:%M}',
            style: {
              color: '$chinagray',
              fontSize: '9px',
            },
          },
          lineWidth: 0,
        },
        yAxis: {
          title: false,
          opposite: false,
          minTickInterval: 0,
          startOnTick: false,
        },
        legend: false,
        tooltip: {
          xDateFormat: '%Y-%m-%d %H:%M:%S',
          split: false,
          shared: true,
          valueDecimals: 0,
          valueSuffix: '개',
          formatter() {
            let tooltip = getDateToString(this.x, 'YYYY-MM-DD HH:mm:ss');
            this.points.forEach((point) => {
              let yValue = convertValueByUnitData(point.y, 'count', '개', 1, null, null, true);
              if (point.series.userOptions.type === 'areasplinerange') {
                yValue = `${convertValueByUnitData(point.point.low, 'count', '개', 1, null, null, true)} - ${convertValueByUnitData(point.point.high, 'count', '개', 1, null, null, true)}`;
              }
              tooltip += `<br/><span style="color:${point.color}">\u25CF</span> ${point.series.name}: <b>${yValue}</b><br/>`;
            });
            return tooltip;
          },
        },
        rangeSelector: false,
        scrollbar: false,
        plotOptions: {
          series: {
            animation: false,
            lineWidth: 1.8,
            marker: {
              radius: 2,
            },
          },
          column: {
            maxPointWidth: 10,
          },
        },
        series: [],
        colors: chartColorSet,
      },
    };
  },
  watch: {
    eventInfo: {
      immediate: true,
      handler(value) {
        if (!isEmpty(value) && value.eventInfo.eventType === 'BASELINE') {
          this.loadChartData(value);
        }
      },
    },
  },
  methods: {
    loadChartData(eventInfo) {
      this.options.series = [];
      let baselineChartDatas;
      this.chartLoading = true;
      const startDateTime = getIntervalDatetimeParams(eventInfo.checkDatetime, 'hour', 6);
      const endDateTime = getIntervalDatetimeParams(eventInfo.checkDatetime, 'hour', -6);

      const params = {
        customFilterId: Number(eventInfo.eventInfo.eventValue),
        startDateTime,
        endDateTime,
        type: 'filter',
      };

      // ES에서 수집된 로그를 시간별 추이 aggs(로그 집계) 데이터로 조회
      LogAPI.getAnalysisHistogramData(params).then((response) => {
        if (response.data.success) {
          const series = response.data.data;
          // series[0].type = 'coloredline';
          if (!isEmpty(series)) {
            series[0].type = 'column';
            let baselineParams = {
              params: {
                filterId: Number(eventInfo.eventInfo.eventValue),
              },
            };
            // 베이스라인 정보 조회(stats_period, upper_bound, lower_bound)
            LogAPI.getBaselineInfo(baselineParams).then((infoResponse) => {
              if (infoResponse.data.success) {
                const baselineInfo = infoResponse.data.data;
                baselineParams = {
                  customFilterId: Number(eventInfo.eventInfo.eventValue),
                  startDateTime,
                  endDateTime,
                  checkDatetime: getIntervalDatetimeParams(endDateTime, 'month', baselineInfo.statsPeriod),
                };
                const gap = new Date(endDateTime).getTime() - new Date(startDateTime).getTime();

                /**
                 * 베이스라인 데이터(H, D)를 조회해 현재 조회 중인 히스토그램 차트 데이터와 동기화하는 로직
                 */
                LogAPI.getBaselineChartData(baselineParams).then((res) => {
                  const baselineData = res.data.data;
                  if (res.data.success && !isEmpty(baselineData)) {
                    const aggData = series[0].data;
                    const baselineChartData = [];
                    const aggs = [];
                    forEach(aggData, (item) => {
                      const chartX = new Date(item[0]); // 차트 데이터의 X축값(datetime)
                      const avg = find(baselineData, { checkDatetime: chartX.getHours() });

                      // 조회해온 베이스라인 데이터의 logCount값을 가공해 차트에 뿌려주기 위해 divider값(number)을 가져옴
                      const valueDivider = this.getValueDividerByTimeInterval(gap);
                      if (!isEmpty(avg)) {
                        // 베이스라인 정보의 upperBound(상한)과 lowerBound(하한)의 값, divider로 나눈 logCount로 베이스라인의 상한값과 하한값을 계산
                        const upperBound = (avg.logCount / valueDivider) * (1 + (baselineInfo.upperBound / 100));
                        const lowerBound = (avg.logCount / valueDivider) * (1 - (baselineInfo.lowerBound / 100));
                        baselineChartData.push({ x: item[0], low: Number(lowerBound.toFixed(0)), high: Number(upperBound.toFixed(0)) });
                        if (item[1] > upperBound || item[1] < lowerBound) {
                          aggs.push({ x: item[0], y: item[1], color: '#Fb4e7c' });
                        } else {
                          aggs.push({ x: item[0], y: item[1] });
                        }
                      } else {
                        aggs.push({ x: item[0], y: item[1] });
                      }
                    });
                    baselineChartDatas = {
                      name: '하한 - 상한',
                      lineWidth: 0,
                      data: baselineChartData,
                      type: 'areasplinerange',
                      fillOpacity: 0.3,
                      zIndex: 0,
                      marker: {
                        enabled: false,
                      },
                    };
                    this.options.series = [{ name: '수집 로그 수', data: aggs, type: 'column' }, baselineChartDatas];
                  }
                }).catch((e) => {
                  this.$message(msgUtil.getResultMsgData(msgUtil.action.VIEW, '베이스라인 정보', false));
                  throw new Error(e);
                });
              }
            }).catch((e) => {
              this.$message(msgUtil.getResultMsgData(msgUtil.action.VIEW, '베이스 라인', false));
              throw new Error(e);
            });
          }
          this.chartLoading = false;
        }
      }).catch((e) => {
        this.$message(msgUtil.getResultMsgData(msgUtil.action.VIEW, '히스토그램', false));
        throw new Error(e);
      });
    },
    /**
     * 실제 Aggs로 조회해온 집계 데이터들의 timestamp 간격을 계산해 베이스라인 logCount를 나눌 값을 구한다.
     * @author Minyeong
     * @param  {[type]} gap [endDatetime.getTime() - startDatetime.getTime()]
     * @return {[type]}     [description]
     */
    getValueDividerByTimeInterval(gap) {
      const DAY3 = 1000 * 60 * 60 * 24 * 3;
      const DAY19 = 1000 * 60 * 60 * 24 * 19;
      const DAY30 = 1000 * 60 * 60 * 24 * 30;
      const DAY90 = 1000 * 60 * 60 * 24 * 90;

      if (gap >= 0 && gap <= DAY3) {
        // gap이 0일 이상 3일 이하일 때 > 조회해오는 로그 집계 데이터의 time interval이 5분이므로 베이스라인 H 데이터의 logCount를 12로 나눔(60/12 = 5)
        return 12;
      } else if (gap > DAY3 && gap <= DAY19) {
        // gap이 3일 이상 19일 이하일 때 > 조회해오는 로그 집계 데이터의 time interval이 30분이므로 베이스라인 H 데이터의 logCount를 2로 나눔(60/2 = 30)
        return 2;
      } else if (gap > DAY19 && gap <= DAY30) {
        // gap이 19일 이상 30일 이하일 때 > 조회해오는 로그 집계 데이터의 time interval이 2시간이므로 베이스라인 H 데이터의 logCount에 2를 곱함(60*2 = 120)
        return 0.5;
      } else if (gap > DAY30 && gap <= DAY90) {
        // gap이 30일 이상 90일 이하일 때 > 조회해오는 로그 집계 데이터의 time interval이 2시간이므로 베이스라인 D 데이터의 logCount를 12로 나눔(24/12 = 2)
        return 12;
      } else if (gap > DAY90) {
        // gap이 90일 이상일 때 > 조회해오는 로그 집계 데이터의 time interval이 1일이므로 베이스라인 D 데이터의 logCount를 그대로 사용함
        return 1;
      }

      return 1;
    },
  },
};
</script>

<style>

</style>



// WEBPACK FOOTER //
// src/views/log-analysis/Event/DetailEventChart.vue